// v3.18-1383 GMAIL-003 — Communications ▸ Mailbox with FULL Gmail
// compose (new / reply / reply-all / forward / drafts / attachments /
// rich-text / from-selector / signature). Sending goes through the
// connected Google mailbox, NOT SMTP2GO. SMTP2GO remains authoritative
// for every automated system email exactly as designed.
import { useEffect, useMemo, useState } from 'react';
import {
  Inbox, Send, FileEdit, Search, RefreshCw, Loader2, Plug, Plus,
  Mail, ChevronDown, ShieldCheck, Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import MailboxMessageList from './MailboxMessageList';
import GmailComposeModal from './GmailComposeModal';

const FOLDERS = [
  { key: 'inbox',  label: 'Inbox',  icon: Inbox },
  { key: 'sent',   label: 'Sent',   icon: Send },
  { key: 'drafts', label: 'Drafts', icon: FileEdit },
  { key: 'trash',  label: 'Trash',  icon: Trash2 },
];

export default function MailboxTab() {
  const navigate = useNavigate();
  const [mailboxes, setMailboxes] = useState([]);
  const [selectedMailboxId, setSelectedMailboxId] = useState('');
  const [folder, setFolder] = useState('inbox');
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeCtx, setComposeCtx] = useState({ mode: 'new', source: null });
  const [refreshTick, setRefreshTick] = useState(0);
  const [counts, setCounts] = useState({ inbox: 0, sent: 0, drafts: 0, trash: 0 });

  const loadCounts = async (mid) => {
    if (!mid) return;
    try {
      const { data } = await api.get(`/gmail/folder-counts?mailbox_id=${mid}`);
      setCounts({ inbox: data.inbox, sent: data.sent, drafts: data.drafts, trash: data.trash });
    } catch { /* soft-fail */ }
  };
  useEffect(() => { if (selectedMailboxId) loadCounts(selectedMailboxId); },
            [selectedMailboxId, refreshTick]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/google/mailboxes');
      const items = data?.items || [];
      setMailboxes(items);
      if (!selectedMailboxId && items.length) setSelectedMailboxId(items[0].id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load mailboxes');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const selectedMailbox = useMemo(
    () => mailboxes.find((m) => m.id === selectedMailboxId) || null,
    [mailboxes, selectedMailboxId],
  );

  const hasNoMailboxes = !loading && mailboxes.length === 0;

  return (
    <div className="space-y-4" data-testid="mailbox-tab">
      {/* Connected Mailbox strip */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-200"
        data-testid="mailbox-connected-strip"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <Mail className="w-4 h-4" />
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Connected Mailbox</div>

          {loading ? (
            <span className="text-sm text-slate-500 flex items-center gap-1">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </span>
          ) : hasNoMailboxes ? (
            <span
              className="text-sm text-amber-800 font-semibold"
              data-testid="mailbox-none-connected"
            >
              No Google mailbox connected
            </span>
          ) : (
            <div className="relative">
              <button
                data-testid="mailbox-switcher-trigger"
                onClick={() => setSwitcherOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-sm font-mono text-slate-800 hover:border-slate-400"
              >
                {selectedMailbox?.email_address || '—'}
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
              {switcherOpen && (
                <div
                  className="absolute z-10 mt-1 min-w-[280px] rounded-lg bg-white border border-slate-200 shadow-lg overflow-hidden"
                  data-testid="mailbox-switcher-menu"
                >
                  {mailboxes.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`mailbox-switch-${m.id}`}
                      onClick={() => { setSelectedMailboxId(m.id); setSwitcherOpen(false); }}
                      className={
                        'block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ' +
                        (m.id === selectedMailboxId ? 'bg-slate-100 font-semibold' : '')
                      }
                    >
                      <div className="font-mono text-slate-800">{m.email_address}</div>
                      <div className="text-[11px] text-slate-500">
                        {m.display_name || '—'} · {m.status}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            data-testid="mailbox-manage"
            onClick={() => navigate('/google-mailboxes')}
            className="text-xs"
          >
            <Plug className="w-3.5 h-3.5 mr-1" /> Manage mailboxes
          </Button>
          <Button
            size="sm"
            onClick={load}
            data-testid="mailbox-refresh"
            className="bg-slate-900 hover:bg-black text-white text-xs"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* No-mailbox helper — friendly message per sprint spec */}
      {hasNoMailboxes && (
        <div
          className="rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 p-4 flex items-start gap-3"
          data-testid="mailbox-connect-cta"
        >
          <Plug className="w-5 h-5 mt-0.5" />
          <div className="text-sm flex-1">
            <div className="font-semibold">Connect a Google mailbox to see Inbox, Sent, and Drafts here.</div>
            <div className="text-xs mt-1 text-amber-800">
              Ops-driven communications will flow through your connected Workspace mailbox.
              Automated system emails (reminders, magic links, contracts, receipts) continue via SMTP2GO — unchanged.
            </div>
          </div>
          <Button
            onClick={() => navigate('/google-mailboxes')}
            className="bg-amber-800 hover:bg-amber-900 text-white text-xs"
            data-testid="mailbox-connect-btn"
          >
            Connect now
          </Button>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* Folder rail */}
        <div className="col-span-12 md:col-span-3 lg:col-span-2">
          <Button
            data-testid="mailbox-compose-btn"
            onClick={() => { setComposeCtx({ mode: 'new', source: null }); setComposeOpen(true); }}
            disabled={hasNoMailboxes}
            className="w-full bg-slate-900 hover:bg-black text-white mb-3"
          >
            <Plus className="w-4 h-4 mr-1" /> Compose
          </Button>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {FOLDERS.map((f) => (
              <button
                key={f.key}
                data-testid={`mailbox-folder-${f.key}`}
                onClick={() => setFolder(f.key)}
                className={
                  'w-full flex items-center gap-2 px-3 py-2.5 text-sm border-b border-slate-100 last:border-b-0 transition ' +
                  (folder === f.key
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-700 hover:bg-slate-50')
                }
              >
                <f.icon className="w-4 h-4" />
                <span className="flex-1 text-left">{f.label}</span>
                <span
                  data-testid={`mailbox-count-${f.key}`}
                  className={
                    'text-[10px] px-1.5 py-0.5 rounded-full font-bold ' +
                    (folder === f.key
                      ? 'bg-white/25 text-white'
                      : 'bg-slate-100 text-slate-500')
                  }
                >
                  {counts[f.key] ?? 0}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3 text-xs text-slate-500">
            <div className="uppercase tracking-wider text-[10px] mb-1 text-slate-400">Signed in as</div>
            <div className="font-mono text-slate-700" data-testid="mailbox-signed-in-as">
              {selectedMailbox?.email_address || '—'}
            </div>
            <div className="mt-2 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-start gap-1">
              <ShieldCheck className="w-3 h-3 mt-0.5" />
              <span>Automated system emails still flow through SMTP2GO — unchanged.</span>
            </div>
          </div>
        </div>

        {/* Message pane */}
        <div className="col-span-12 md:col-span-9 lg:col-span-10">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 capitalize">
                {FOLDERS.find((f) => f.key === folder)?.label || folder}
                {selectedMailbox && (
                  <span className="text-xs font-normal text-slate-500 font-mono">
                    · {selectedMailbox.email_address}
                  </span>
                )}
              </div>
              <div className="relative w-72">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="mailbox-search"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Search messages…"
                  className="pl-7 h-8 text-sm"
                />
              </div>
            </div>

            <div data-testid="mailbox-live-pane">
              {selectedMailbox ? (
                <MailboxMessageList
                  key={`${selectedMailbox.id}-${folder}-${refreshTick}`}
                  mailbox={selectedMailbox}
                  folder={folder}
                  search={searchQ}
                  onReply={(msg) => { setComposeCtx({ mode: 'reply', source: msg });     setComposeOpen(true); }}
                  onReplyAll={(msg) => { setComposeCtx({ mode: 'replyAll', source: msg }); setComposeOpen(true); }}
                  onForward={(msg) => { setComposeCtx({ mode: 'forward', source: msg }); setComposeOpen(true); }}
                  onDeleted={() => { setRefreshTick((n) => n + 1); loadCounts(selectedMailbox.id); }}
                />
              ) : (
                <div className="p-10 text-center" data-testid="mailbox-phase1-empty">
                  <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-3">
                    <Mail className="w-6 h-6 text-slate-400" />
                  </div>
                  <div className="text-sm font-semibold text-slate-800">
                    Connect a Google mailbox to see Inbox, Sent, and Drafts here.
                  </div>
                  <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px]">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    SMTP2GO remains authoritative for every automated outbound email
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Compose — v3.18-1383 GMAIL-003 full compose modal. Sends via
          the connected Google mailbox. SMTP2GO untouched. */}
      <GmailComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        mailboxes={mailboxes}
        defaultMailboxId={selectedMailboxId}
        mode={composeCtx.mode}
        sourceMessage={composeCtx.source}
        onSent={async (result) => {
          // After a real send, force the sync engine to catch up and
          // refresh the message list so Sent/Drafts reflect Gmail.
          if (selectedMailbox?.id) {
            try {
              await api.post(`/gmail/sync/${selectedMailbox.id}`, { mode: 'incremental' });
            } catch { /* soft-fail; the list will still refresh */ }
            if (result?.status === 'drafted') setFolder('drafts');
            else if (result?.status === 'sent')   setFolder('sent');
            setRefreshTick((n) => n + 1);
          }
        }}
      />
    </div>
  );
}
